import { useCallback, useMemo, useState } from "react";
import {
  Box,
  DataTable,
  DataTableGroupByDropdown,
  DataTableToolbar,
  DataTableToolbarActions,
  DataTableToolbarLeft,
  Icon,
  Text,
  UNASSIGNED_GROUP_KEY,
  renderDefaultGroupActions,
  useDataTableState,
  useWidgetSurface,
  type BulkAction,
  type DataTableGroupFieldConfig,
  type DataTableGroupSortDirection,
  type DataTableGroupType,
  type WidgetInstance,
} from "@salestrak/ui";
import { ExpandableSearch } from "@salestrak/ui";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { EntityTag } from "#app/components/entity";
import { GroupQueryProvider, useGroupStates } from "#app/lib/hooks/group-query-provider";
import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import { useIssueColumns } from "#app/components/issues/issue-columns";
import {
  buildGroupWhere,
  ISSUE_GROUP_SERVER_KEY,
  type IssueGroupDimension,
} from "#app/components/issues/issue-grouping";
import {
  BulkAssigneeContent,
  BulkStageContent,
  type BulkIssueMutations,
} from "#app/components/issues/issue-bulk-actions";
import { useIssueView } from "#app/components/issue-view";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useIssueListMutations } from "#app/hooks/issue-list/use-issue-list-mutations";
import { useRefetchIssueGroups } from "#app/hooks/issue-list/use-refetch-issue-groups";
import { canEdit } from "#app/lib/utils/access-level";
import { AnalyticsScope, AnalyticsView } from "#app/lib/analytics";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { ISSUE_SEARCH_FIELDS } from "#app/lib/filters/list-configs";
import { type IssueRow, IssuesDocument, type StageOption } from "#app/lib/data";
import { buildSearchWhere, combineWhereConditions } from "#app/lib/query-utils";
import { ENTITY_KIND_ICON, resolveEntityLabel, type EntityData } from "#app/lib/entity-visuals";
import { AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
// Relative imports (not the `features/dashboards` barrel) to avoid a cycle: the
// barrel re-exports the detail dialog, which renders this panel.
import { DRILLDOWN_ORDER_BY, useWidgetDrilldownWhere } from "./use-widget-drilldown-issues";
import { useDashboardScope } from "./scope/dashboard-scope-context";
import type { WidgetDataSlice } from "./widget-drilldown";
import type { Omittable } from "@salestrak/ui";

/**
 * The "Data" tab of the widget detail dialog: the drill-down into the entities
 * feeding the widget. The data layer ({@link useWidgetDrilldownWhere})
 * reproduces the widget's own query window as ONE folded `where` (a clicked
 * chart element folds in as an exact server predicate), and every table group
 * is its own windowed paged query over it — so the drawer reaches EVERY row
 * feeding the widget, never a capped sample. Grouped mode resolves its group
 * set from the exact server `issueGroupCounts` aggregation, and search is a
 * server predicate over the app-wide issue search columns.
 *
 * Rows render through the real {@link DataTable} with the shared issue columns
 * (name / stage / age — the identity subset that fits the drawer); the
 * toolbar mirrors the workflow list view (group-by left, search right).
 */

/** Columns the drawer has room for at its default width — the identity subset. */
const DRAWER_COLUMN_IDS = new Set(["name", "stage", "age"]);

// The fields the Data-tab table can group by — exactly the data the drawer's
// rows carry, matching the workflow list view's group-by options.
const GROUP_FIELDS: Record<string, DataTableGroupFieldConfig> = {
  stage: { label: "Stage", icon: "circle" },
  assignees: { label: "Assignee", icon: ENTITY_KIND_ICON.user },
  age: { label: "Age", icon: "clock" },
};

// The drawer's group fields → the shared grouping dimensions (the table's
// `assignees` field key is the shared module's `assignee` dimension).
const FIELD_TO_DIMENSION: Record<string, IssueGroupDimension> = {
  stage: "stage",
  assignees: "assignee",
  age: "age",
};

// Age buckets ordered best→worst, mirroring the board's age pills.
const AGE_GROUP_ORDER: Record<string, number> = {
  onTrack: 0,
  atRisk: 1,
  offTrack: 2,
  disabled: 3,
};

const getIssuesConnection = (data: any) => data?.issues;
const PAGE_LIMIT = 50;

/** How to render each group's header — resolved with the group set so the title
 * renderer routes through the entity chip system without re-deriving rows. */
type GroupMeta =
  | { kind: "stage"; stage: StageOption }
  | { kind: "assignee"; entity: EntityData }
  | { kind: "age"; status: AgeStatus }
  | { kind: "unassigned"; label: string };

/** One drill-down group: key/label/meta, exact server total, folded `where`. */
type DrilldownGroupDef = {
  key: string;
  label: string;
  totalCount: number;
  where: Record<string, unknown> | undefined;
  meta: GroupMeta | null;
};

export function WidgetDataPanel({
  instance,
  slice,
  onClearSlice,
}: Omittable<{
  instance: WidgetInstance;
  /** Chart-element drill-down filter — set when a slice/bar was clicked. */
  slice?: WidgetDataSlice | null;
  onClearSlice?: () => void;
}>) {
  // The widget's own data window, reproduced as one folded `where` — a clicked
  // slice is already an exact server predicate inside it.
  const { where: drilldownWhere, variablesKey } = useWidgetDrilldownWhere(instance, slice);

  // The surface the detail dialog's panel painted — the table's sticky header
  // and pinned-column masks must paint the same fill.
  const panelSurface = useWidgetSurface();

  // Stage labels resolve from every workspace stage — the window can span
  // workflows, and the Stage column must never render "Unknown" for a row in
  // another workflow.
  const { stages: workspaceStages, userMap, teamMap } = useWorkspaceData();
  // `useWorkspaceData()` stages ARE the stage row — no re-shaping.
  const availableStages: StageOption[] = workspaceStages;
  const stageById = useMemo(() => {
    const map = new Map<string, StageOption>();
    for (const s of availableStages) map.set(s.stageId, s);
    return map;
  }, [availableStages]);

  // Row interactions — the same live cells as the list view: open, stage
  // change, assignees, age dialog. No address handlers → no Address column.
  const { openIssue } = useIssueView();
  const handleOpenIssue = useCallback(
    (issue: IssueRow) => openIssue(issue),
    [openIssue],
  );
  const { changeStage, updateAssignees, bulk } = useIssueListMutations();
  const bulkMutations: BulkIssueMutations = bulk;
  const [ageDialogIssue, setAgeDialogIssue] = useState<IssueRow | null>(null);
  const handleAgePress = useCallback((issue: IssueRow) => setAgeDialogIssue(issue), []);
  const refetchGroups = useRefetchIssueGroups();

  // Bulk actions mirror the list view: move-to-stage only under a single-
  // workflow dashboard (a cross-workflow selection has no single stage
  // target); assignees for any scope. Every commit runs on the selected row
  // ids — select-all-by-`where` never engages.
  const scope = useDashboardScope();
  const analyticsScope =
    scope?.entity?.type === "workflow"
      ? AnalyticsScope.Workflow
      : scope?.entity?.type === "team"
        ? AnalyticsScope.Team
        : AnalyticsScope.Member;
  const scopeWorkflowId = scope?.entity?.type === "workflow" ? scope.entity.id : undefined;
  const bulkActions = useMemo<BulkAction<IssueRow>[]>(() => {
    const actions: BulkAction<IssueRow>[] = [];

    if (scopeWorkflowId) {
      actions.push({
        id: "change-stage",
        type: "popover",
        label: "Move to stage",
        icon: "layers",
        contentProps: { padding: "none" },
        content: ({ selectedRows, commit }) => (
          <BulkStageContent
            view={AnalyticsView.WidgetView}
            scope={analyticsScope}
            workflowId={selectedRows[0]?.workflowId ?? scopeWorkflowId}
            commit={commit}
            bulkMutations={bulkMutations}
            refetchGroups={refetchGroups}
            availableStages={availableStages}
            selectedCount={selectedRows.length}
          />
        ),
      });
    }

    actions.push({
      id: "set-assignees",
      type: "popover",
      label: "Assignees",
      icon: "users",
      contentProps: { padding: "none", width: 384 },
      content: ({ selectedRows }) => (
        <BulkAssigneeContent
          view={AnalyticsView.WidgetView}
          scope={analyticsScope}
          bulkMutations={bulkMutations}
          selectedRows={selectedRows}
        />
      ),
    });

    return actions;
  }, [scopeWorkflowId, analyticsScope, bulkMutations, refetchGroups, availableStages]);

  const rawColumns = useIssueColumns({
    availableStages,
    onOpenIssue: handleOpenIssue,
    onChangeStage: changeStage,
    onAssigneesChange: updateAssignees,
    onAgePress: handleAgePress,
  });
  const drawerColumns = useMemo(
    () =>
      rawColumns.filter((c) => {
        const id = c.id ?? (c as { accessorKey?: string }).accessorKey;
        return id != null && DRAWER_COLUMN_IDS.has(String(id));
      }),
    [rawColumns],
  );

  const {
    columns,
    groupByField,
    setGroupByField,
    isGrouped,
    groupByLabels,
    groupByIcons,
    groupSortDirection,
    setGroupSortDirection,
  } = useDataTableState({
    columns: drawerColumns,
    groupFields: GROUP_FIELDS,
    defaultGroupByField: "none",
    defaultGroupSortDirection: "asc",
  });

  // Search is a SERVER predicate over the app-wide issue search columns —
  // debounced, folded into every group's query, so it narrows the whole
  // window rather than the loaded page.
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const effectiveWhere = useMemo(() => {
    const searchWhere = buildSearchWhere(debouncedSearch, ISSUE_SEARCH_FIELDS);
    return searchWhere
      ? (combineWhereConditions([drilldownWhere, searchWhere]) ?? drilldownWhere)
      : drilldownWhere;
  }, [drilldownWhere, debouncedSearch]);

  // Grouped mode: the group SET comes from the exact server aggregation over
  // the same folded `where`; each non-empty group pages its own windowed query.
  const dimension = isGrouped ? FIELD_TO_DIMENSION[groupByField] : undefined;
  const groupCounts = useIssueGroupCounts({
    where: dimension ? effectiveWhere : undefined,
    groupBy: dimension ? ISSUE_GROUP_SERVER_KEY[dimension] : undefined,
  });
  const groupDefs = useMemo<DrilldownGroupDef[]>(() => {
    if (!dimension) {
      // "Grouped by nothing": one real group (NOT the ungrouped sentinel) so
      // the DataTable keeps the header, count, and collapse affordance.
      return [
        { key: "all", label: "All Projects", totalCount: 0, where: effectiveWhere, meta: null },
      ];
    }
    if (!groupCounts.ready) return [];

    type Sortable = DrilldownGroupDef & { sort: number | string };
    const defs: Sortable[] = Object.entries(groupCounts.counts)
      .filter(([, total]) => total > 0)
      .map(([key, totalCount]) => {
        const where = combineWhereConditions(
          [effectiveWhere, buildGroupWhere(dimension, key)].filter(
            (c): c is Record<string, unknown> => c != null,
          ),
        );
        if (dimension === "stage") {
          const stage = key === UNASSIGNED_GROUP_KEY ? undefined : stageById.get(key);
          return stage
            ? { key, label: stage.name, totalCount, where, meta: { kind: "stage", stage } as const, sort: stage.sortOrder }
            : {
                key,
                label: "No stage",
                totalCount,
                where,
                meta: { kind: "unassigned", label: "No stage" } as const,
                sort: Number.MAX_SAFE_INTEGER,
              };
        }
        if (dimension === "assignee") {
          if (key === UNASSIGNED_GROUP_KEY) {
            // Named assignees sort alphabetically; unassigned last (￿ sorts high).
            return {
              key,
              label: "No assignee",
              totalCount,
              where,
              meta: { kind: "unassigned", label: "No assignee" } as const,
              sort: "￿",
            };
          }
          const entity: EntityData = { kind: "user", id: key, user: userMap.get(key) ?? null };
          const label = userMap.has(key)
            ? resolveEntityLabel(entity)
            : (teamMap.get(key)?.teamName ?? "Unknown");
          return { key, label, totalCount, where, meta: { kind: "assignee", entity } as const, sort: label };
        }
        // age
        const status = key as AgeStatus;
        return AGE_STATUS_CONFIG[status] && key !== UNASSIGNED_GROUP_KEY
          ? {
              key,
              label: AGE_STATUS_CONFIG[status].label,
              totalCount,
              where,
              meta: { kind: "age", status } as const,
              sort: AGE_GROUP_ORDER[key] ?? 98,
            }
          : {
              key,
              label: "No age",
              totalCount,
              where,
              meta: { kind: "unassigned", label: "No age" } as const,
              sort: 99,
            };
      });

    defs.sort((a, b) => {
      const cmp =
        typeof a.sort === "number" && typeof b.sort === "number"
          ? a.sort - b.sort
          : String(a.sort).localeCompare(String(b.sort));
      return groupSortDirection === "desc" ? -cmp : cmp;
    });
    return defs.map(({ sort: _sort, ...def }) => def);
  }, [
    dimension,
    groupCounts.ready,
    groupCounts.counts,
    effectiveWhere,
    stageById,
    userMap,
    teamMap,
    groupSortDirection,
  ]);
  const metaByKey = useMemo(() => {
    const map: Record<string, GroupMeta> = {};
    for (const def of groupDefs) if (def.meta) map[def.key] = def.meta;
    return map;
  }, [groupDefs]);

  // Each group pages through its own query provider (mounted below); the table
  // groups read the reported windows, mirroring the full-page tables.
  const { states, registerState, getGroupProps } = useGroupStates<IssueRow>();
  const groups = useMemo<DataTableGroupType<IssueRow>[]>(
    () =>
      groupDefs.map((def) => ({
        key: def.key,
        label: def.label,
        totalCount: states.get(def.key)?.total ?? (dimension ? def.totalCount : undefined),
        ...getGroupProps(def.key),
      })),
    [groupDefs, dimension, states, getGroupProps],
  );
  const tableLoading = dimension
    ? !groupCounts.ready
    : (states.get("all")?.loading ?? true);

  const renderGroupTitle = useCallback(
    (groupKey: string) => {
      const m = metaByKey[groupKey];
      if (!m) return groupKey;
      if (m.kind === "unassigned") {
        return (
          <Text size="sm" variant="muted">
            {m.label}
          </Text>
        );
      }
      if (m.kind === "stage") {
        return (
          <Box direction="row" align="center" gap="sm" overflow="hidden">
            <EntityTag
              entity={{ kind: "stage", id: m.stage.stageId, stage: m.stage }}
              interactive={false}
            />
          </Box>
        );
      }
      if (m.kind === "assignee") {
        return (
          <Box direction="row" align="center" gap="sm" overflow="hidden">
            <EntityTag entity={m.entity} interactive={false} />
          </Box>
        );
      }
      const ageInfo = AGE_STATUS_CONFIG[m.status];
      return (
        <Box direction="row" align="center" gap="sm" overflow="hidden">
          {/* `decoColor` — the palette hue the age chart slices paint, so the
              chip clock matches the slice it drills from. */}
          <Icon
            name="clock"
            size="md"
            color={ageInfo.decoColor === "grey" ? "muted" : ageInfo.decoColor}
          />
          <Box fill="flex" shrink>
            <Text size="sm" weight="medium" truncate={1}>
              {ageInfo.label === "Disable age" ? "Disabled" : ageInfo.label}
            </Text>
          </Box>
        </Box>
      );
    },
    [metaByKey],
  );

  return (
    <Box direction="column" fill="flex" overflow="hidden" gap="md">
      {groupDefs.map((def) => (
        <GroupQueryProvider<IssueRow>
          key={def.key}
          groupKey={def.key}
          document={IssuesDocument}
          variables={{ where: def.where, orderBy: DRILLDOWN_ORDER_BY, limit: PAGE_LIMIT }}
          getConnection={getIssuesConnection}
          onStateChange={registerState}
        />
      ))}
      <DataTableToolbar>
        <DataTableToolbarLeft>
          <DataTableGroupByDropdown
            groupByField={groupByField}
            setGroupByField={setGroupByField}
            groupByLabels={groupByLabels}
            groupByIcons={groupByIcons}
            groupSortDirection={groupSortDirection}
            setGroupSortDirection={setGroupSortDirection}
            iconOnly
          />
          {slice ? (
            <FilterToggle
              size="xs"
              icon="filter"
              label={slice.label}
              selected
              onClear={onClearSlice}
            />
          ) : null}
        </DataTableToolbarLeft>
        <DataTableToolbarActions>
          <ExpandableSearch onValueChange={setSearch} placeholder="Search projects..." size="xs" />
        </DataTableToolbarActions>
      </DataTableToolbar>

      <Box direction="column" fill="flex" overflow="hidden">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={(row) => row.issueId}
          loading={tableLoading}
          stickyHeader
          density="regular"
          fillColumnId="name"
          paddingX="md"
          paddingXAlign="content"
          surface={panelSurface}
          groupTitle={isGrouped ? renderGroupTitle : undefined}
          groupActions={renderDefaultGroupActions}
          bulkActions={bulkActions}
          canSelectRow={(issue) => canEdit(issue.viewerAccessLevel)}
        />
      </Box>

      {ageDialogIssue && (
        <UpdateAgeDialog
          open
          onOpenChange={(open) => {
            if (!open) setAgeDialogIssue(null);
          }}
          issueId={ageDialogIssue.issueId}
          age={ageDialogIssue.age}
          stageId={ageDialogIssue.stageId}
        />
      )}
    </Box>
  );
}
